import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = ["innerHTML"]

import { useDialogPluginComponent } from "quasar"
import SfxBtn from "src/components/button/SfxBtn.vue"
import SfxBtnOutline from "src/components/button/SfxBtnOutline.vue"
import SfxCardIssueCondensed from "src/components/card/SfxCardIssueCondensed.vue"
import SfxEmptyPage from "src/components/empty/SfxEmptyPage.vue"
import SfxSkeleton from "src/components/special/SfxSkeleton.vue"
import { getChangesDB } from "src/db/databases"
import router from "src/router"
import { useCacheStore } from "src/stores/cacheStore"
import { useFilterStore } from "src/stores/filterStore"
import { useUnsubmittedWorkStore } from "src/stores/unsubmittedWork"
import { SFX_CARD_CONDENSED_HEIGHT } from "src/util/constants/configurations"
import { EmptyStates } from "src/util/constants/emptyStates"
import { MissingEntityType } from "src/util/enums/entityType"
import { NamedManagerPath, NamedPath } from "src/util/enums/namedPaths"
import {
  fetchIssuePriorityListData,
  fetchWorkDomainWorkspaceListData,
} from "src/util/functions/entityData"
import { checkScan } from "src/util/functions/identifier"
import {
  getIssueCardData,
  getOpenCorrectiveWorkOnItemForIssue,
  setOverdueTime,
  sortOpenIssuesData,
} from "src/util/functions/issues"
import { showErrorNotification, showWarningNotification } from "src/util/functions/notifications"
import { getUnsubmittedAttachments, getUnsubmittedWork } from "src/util/functions/unsubmittedData"
import { getWorkDomainWorkspaceData } from "src/util/functions/workDomain"
import {
  IssueAssociationType,
  IssueCardData,
  IssueEntity,
  IssuesOnItem,
} from "src/util/types/dataModels/issue"
import { CorrectiveWorkOnItem } from "src/util/types/dataModels/performedWork"
import { UnsubmittedWork } from "src/util/types/dataModels/unsubmittedWork"
import { computed, onMounted, PropType, ref, watch } from "vue"
import { useI18n } from "vue-i18n"

//region Props and Emits


export default /*#__PURE__*/_defineComponent({
  __name: 'SfxDialogListIssue',
  props: {
  associationType: {
    type: String as PropType<IssueAssociationType>,
    required: true,
  },

  entity: {
    type: Object as PropType<IssueEntity>,
    required: true,
  },

  // The list should be an ordered list
  list: {
    type: Array as PropType<IssuesOnItem[]>,
    required: true,
  },

  areUnsavedChanges: {
    type: Boolean,
    required: false,
  },

  onDismiss: {
    type: Function,
    required: false,
  },
},
  setup(__props) {

const props = __props

//endregion

//region Variables

const { t } = useI18n()
const cacheStore = useCacheStore()
const { dialogRef, onDialogCancel } = useDialogPluginComponent()
const elementSelected = ref<IssueCardData | undefined>(undefined)
const issuesList = ref<IssueCardData[]>()
const correctiveWorkOnItem = ref<CorrectiveWorkOnItem | undefined>()
const loadingData = ref(true)
const unsubmittedWorkOnIssue = ref<UnsubmittedWork | undefined>()

const subtitle = t("sfxDialogListIssue.text_more_than_one_issue")

const leftButtonText = computed(() =>
  elementSelected.value ? t("sfxButtons.btn_open_issue") : t("sfxButtons.btn_cancel")
)
const rightButtonText = ref(t("sfxButtons.btn_to_issues"))

const leftButtonFunction = computed(() => (elementSelected.value ? goToIssue : clickCancel))

//endregion

//region Lifecycle

onMounted(async () => {
  await fetchWorkDomainWorkspaceListData()
  await fetchIssuePriorityListData()
  const issueData = await Promise.all(
    props.list.map(async (issue) => {
      let issueData = cacheStore.issueCard.get(issue.id)

      if (!issueData) {
        issueData = await getIssueCardData(issue)
        cacheStore.issueCard.set(issue.id, issueData)
      }

      setOverdueTime(issueData)

      return issueData
    })
  )

  issuesList.value = issueData.sort(sortOpenIssuesData)

  loadingData.value = false
})

//endregion

//region Functions

async function determineRightButtonText(issueClicked: IssueCardData) {
  const changesDb = await getChangesDB()
  const unsubmittedWork = await changesDb.unsubmitted_work
    .findOne({
      selector: { entity_id: issueClicked.issueData.id },
    })
    .exec()

  unsubmittedWork
    ? (rightButtonText.value = t("sfxButtons.btn_resume"))
    : (rightButtonText.value = t("sfxButtons.btn_correct_issue"))

  unsubmittedWorkOnIssue.value = unsubmittedWork?.toMutableJSON()
}

function clickCancel() {
  onDialogCancel()
}

function checkUnsavedChanges(action: () => void) {
  if (props.areUnsavedChanges) {
    onDialogCancel()
    if (typeof props.onDismiss === "function") {
      props.onDismiss(action)
    }
  } else {
    action()
  }
}

async function goToIssue() {
  checkUnsavedChanges(goToIssueRoutePush)
}

async function goToIssueRoutePush() {
  const issueId = elementSelected.value?.issueData.id
  await router.push({ name: NamedPath.ISSUE, params: { issueId } })
}

function goToIssueList() {
  checkUnsavedChanges(goToIssuesListRoutePush)
}

function goToIssuesListRoutePush() {
  const filterStore = useFilterStore()
  // Set the values in the filter store
  filterStore.issuesPageFilters.hasFilters = true
  filterStore.issuesPageFilters.systemLink = {
    entityType: "item",
    entityId: props.entity?.id,
    entityDesignation: props.entity?.designation,
  }

  router.push({
    name: NamedManagerPath.SELECT_ISSUE,
    query: {
      filters: JSON.stringify(filterStore.issuesPageFilters),
    },
  })
}

async function clickCorrectIssue() {
  if (correctiveWorkOnItem.value) {
    const msg = t("notifications.corrective_work_already_exists", {
      correctiveWorkTitle: correctiveWorkOnItem.value?.title,
    })
    showWarningNotification(msg)
  } else {
    const workDomain =
      elementSelected.value?.workDomainData ??
      (await getWorkDomainWorkspaceData(elementSelected.value!.issueData.work_domain))

    // In theory it should not be possible to have issue without item data, due to how getIssueCardData works
    if (workDomain?.require_proof_of_presence && elementSelected.value?.actionableItem) {
      const result = await checkScan({
        entityType: "item",
        entityName: elementSelected.value?.actionableItem.designation,
        entityId: elementSelected.value?.actionableItem.id,
      })

      if (!result) {
        // User does not want to/failed to verify
        showErrorNotification(t("sfxErrorMsg.error_proof_of_presence_required"))
        return
      }
    }

    await router.push({
      name: NamedPath.ADHOC_CORRECTIVE_WORK,
      params: { issueId: elementSelected.value?.issueData.id },
    })
  }
}

async function handleUnsubmittedWork() {
  const issueId = elementSelected.value?.issueData.id

  // Redirect to the prefilled Perform Corrective Work page
  if (issueId && unsubmittedWorkOnIssue.value) {
    const unsubmittedWorkStore = useUnsubmittedWorkStore()
    const unsubmittedWork = await getUnsubmittedWork(unsubmittedWorkOnIssue.value.id)

    // Set the values in the store, so the page form fields are filled at the page load
    if (unsubmittedWork) {
      unsubmittedWorkStore.unsubmittedWork = unsubmittedWork
      unsubmittedWorkStore.unsubmittedImages =
        (await getUnsubmittedAttachments(unsubmittedWork.attachments)) || []

      return await router.push({
        name: NamedPath.ADHOC_CORRECTIVE_WORK,
        params: { issueId: issueId },
      })
    } else return
  }
}

async function rightButtonFunction() {
  if (rightButtonText.value === t("sfxButtons.btn_to_issues")) {
    goToIssueList()
  } else if (rightButtonText.value === t("sfxButtons.btn_correct_issue")) {
    checkUnsavedChanges(clickCorrectIssue)
  } else {
    await handleUnsubmittedWork()
  }
}

async function issueClick(issueClicked: IssueCardData) {
  elementSelected.value =
    elementSelected.value?.issueData.id === issueClicked.issueData.id ? undefined : issueClicked

  await determineRightButtonText(issueClicked)
}

function determineClass(issue: IssueCardData) {
  return elementSelected.value?.issueData.id === issue.issueData.id
    ? "sfx-card--selected"
    : undefined
}

//endregion

//region Setup Code

watch(
  () => elementSelected.value,
  async () => {
    const issueId = elementSelected.value?.issueData.id
    if (issueId) correctiveWorkOnItem.value = await getOpenCorrectiveWorkOnItemForIssue(issueId)
  }
)

//endregion

return (_ctx: any,_cache: any) => {
  const _component_q_card_section = _resolveComponent("q-card-section")!
  const _component_q_card_actions = _resolveComponent("q-card-actions")!
  const _component_q_card = _resolveComponent("q-card")!
  const _component_q_dialog = _resolveComponent("q-dialog")!

  return (_openBlock(), _createBlock(_component_q_dialog, {
    ref_key: "dialogRef",
    ref: dialogRef
  }, {
    default: _withCtx(() => [
      _createVNode(_component_q_card, { class: "q-dialog-plugin sfx-dialog-list" }, {
        default: _withCtx(() => [
          _createVNode(_component_q_card_section, { class: "text-h3" }, {
            default: _withCtx(() => [
              _createElementVNode("span", null, _toDisplayString(_ctx.$t("sfxDialogListIssue.title_open_issues_on_item")), 1)
            ]),
            _: 1
          }),
          _createVNode(_component_q_card_section, { class: "text-body1" }, {
            default: _withCtx(() => [
              _createElementVNode("span", { innerHTML: _unref(subtitle) }, null, 8, _hoisted_1)
            ]),
            _: 1
          }),
          _createVNode(_component_q_card_section, { class: "sfx-dialog-list-elements" }, {
            default: _withCtx(() => [
              (loadingData.value)
                ? (_openBlock(), _createBlock(SfxSkeleton, {
                    key: 0,
                    height: _unref(SFX_CARD_CONDENSED_HEIGHT),
                    "no-of-cards": 3
                  }, null, 8, ["height"]))
                : (issuesList.value?.length === 0)
                  ? (_openBlock(), _createBlock(SfxEmptyPage, {
                      key: 1,
                      class: "q-py-xl",
                      missingEntity: _unref(EmptyStates).get(_unref(MissingEntityType).ISSUE_NOT_FOUND)
                    }, null, 8, ["missingEntity"]))
                  : (issuesList.value?.length > 0)
                    ? (_openBlock(true), _createElementBlock(_Fragment, { key: 2 }, _renderList(issuesList.value, (issue, index) => {
                        return (_openBlock(), _createBlock(SfxCardIssueCondensed, {
                          key: index,
                          issue: issue,
                          onClick: ($event: any) => (issueClick(issue)),
                          class: _normalizeClass(determineClass(issue))
                        }, null, 8, ["issue", "onClick", "class"]))
                      }), 128))
                    : _createCommentVNode("", true)
            ]),
            _: 1
          }),
          _createVNode(_component_q_card_actions, { class: "q-px-md q-pb-md" }, {
            default: _withCtx(() => [
              _createVNode(SfxBtnOutline, {
                onClick: leftButtonFunction.value,
                label: leftButtonText.value,
                class: "col q-mr-sm",
                disable: loadingData.value
              }, null, 8, ["onClick", "label", "disable"]),
              _createVNode(SfxBtn, {
                onClick: rightButtonFunction,
                label: rightButtonText.value,
                class: "col",
                disable: loadingData.value
              }, null, 8, ["label", "disable"])
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 512))
}
}

})